import { Form } from './form';
import { HubSpotValidator } from '../validators/hubspot-validator';
import { HubSpotView } from '../view/hubspot-view';
import { FormTypes } from './form-types';
import { Config } from '../config';
import { Order } from '../config/order';
import {HubspotProvider} from '../providers/hubspot-provider';
import { Util } from '../config/utils';

declare const hbspt: any;
declare const HSFR: any;
declare const window: any;

export class HubSpotForm extends Form {

    private hubSpotApi: any = {};
    hubSpotFormDOM: any = {};
    private ipAddress = '';
    private hutk = '';
    private redirectUrl: any;
    view: HubSpotView = new HubSpotView();
    type = FormTypes.Hubspot;
    hubspotProvider: HubspotProvider = new HubspotProvider();

    constructor(step: number | null = null) {
        super();
        this.step = step;
        this.validators.push(new HubSpotValidator());
    }

    async init(config: Config, callback: any): Promise<any> {
        await super.init(config, callback);
        this.fetchHubSpotFormRedirectUrl();
        this.hutk = this.getCookie('hubspotutk') || '';
        return this.loadScript(`//js.hsforms.net/forms/embed/v2.js`);
    }

    async render() {

        this.jqueryStub();
        await new Promise<void>(resolve => {

            const formParams: any = {
                portalId: this.config.user.hubSpotPortalId,
                formId: this.config.forms.hubspot.id,
                cssClass: 'df-f df-f-hubspot hs-form stacked',
                target: '#df-card-container',
                onFormReady: ($form: any) => {
                    resolve();
                },
                onFormSubmit: () => false
            };

            if (this.config.settings.cssFile !== false) {
                formParams.css = '';
            }

            this.hubSpotApi = hbspt.forms.create(formParams);
        })
            .then(() => {
                const hubSpotFormId = this.config.forms.hubspot.id;
                this.hubSpotFormDOM = this.view.getFromHSFrame(`#hsForm_${hubSpotFormId}`);
                this.removeFormActionUrl();
                this.validationContext = {
                    hubSpotApi: this.hubSpotApi,
                    hubSpotView: this.view
                };
                this.addFormSubmitPreventEvent();
                this.addFormSubmitPreventEventOnEnterKey();
                this.view.hideHubSpotSubmitButton(this.hubSpotFormDOM);

                if (this.step) {
                    this.hideForm();
                }

                Util.getCountryCode().then((res: any) => {
                    this.ipAddress = res.ip;
                });

            });
    }

    removeFormActionUrl() {
        this.hubSpotFormDOM.setAttribute('action', '');
    }

    addFormSubmitPreventEvent = () => {
        this.hubSpotFormDOM.addEventListener('submit', (e: any) => {
            e.preventDefault();
        });
    }

    addFormSubmitPreventEventOnEnterKey = () => {
        const inputs = this.hubSpotFormDOM.querySelectorAll('input');
        if (!inputs) {
            return;
        }
        for (let i = 0; i < inputs.length; i++) {
            const input = inputs[i];
            input.addEventListener('keypress', (e: any) => {
                if (e.which === 10 || e.which === 13) {
                    e.preventDefault();
                    this.config.widget.submit();
                }
            });
        }
    }

    private convertFileToBytes(file: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.onloadend = () => {
                const fileObject = {
                    fieldName: file.fieldName,
                    fileName: file.name,
                    bytes: btoa(fileReader.result as string)
                };
                resolve(fileObject);
            };
            fileReader.readAsBinaryString(file);
        });
    }

    private jqueryStub(): void {
        window['jQuery'] = window['jQuery'] || (() => ({
            // these are all methods required by HubSpot
            change: () => {
            },
            trigger: () => {
            }
        }));
    }

    async submit(order: Order) {
        const serializedFields = this.serializeForm();

        order.params.push(serializedFields);

        order.hscontext = JSON.stringify({
            pageUrl: document.URL,
            pageName: document.title,
            hutk: this.getCookie('hubspotutk'),
            ipAddress: this.ipAddress
        });

        order.hsFormId = this.config.forms.hubspot.id;

        const fileInputFields = this.getFileInputs();
        if (fileInputFields.length > 0) {
            const files = this.view.findFiles(fileInputFields);
            const promises: Promise<any>[] = [];
            files.forEach((file) => {
                promises.push(this.convertFileToBytes(file));
            });
            await Promise.all(promises)
                .then((convertedFiles) => {
                    order.files = convertedFiles;
                });
        }

        return Promise.resolve(order);
    }

    private getFileInputs() {
        return this.hubSpotFormDOM.querySelectorAll('input[type="file"]');
    }

    private serializeForm(): { [key: string]: string } {
        const formFields = this.getAllInputFields();
        this.addDependentFields(this.hubSpotFormDOM.elements, formFields);
        formFields.push('hs_context');

        return formFields.reduce((accumulator: any, current: string) => {

            const node = this.hubSpotFormDOM[current];

            if (node && node.type !== 'file') {
                if (node.length > 0 && !node.type) {

                    accumulator[current] = [];

                    Array.prototype.forEach.call(node, (n: any) => {
                        if (n.checked) {
                            accumulator[current].push(n.value);
                        }
                    });

                } else if (node.type === 'checkbox') {
                    accumulator[current] = node.checked;
                } else {
                    accumulator[current] = node.value;
                }
            }

            return accumulator;
        }, {});
    }

    getAllInputFields(): string[] {
        const inputFields = this.hubSpotFormDOM.querySelectorAll('.hs-input');
        const uniqueInputFieldNames: string[] = [];
        for (let i = 0; i < inputFields.length; i++) {
            const inputFieldName = inputFields[i].name;
            if (uniqueInputFieldNames.indexOf(inputFieldName) === -1) {
                uniqueInputFieldNames.push(inputFieldName);
            }
        }
        return uniqueInputFieldNames;
    }

    private addDependentFields(formNodeElements: any, formFields: string[]) {
        if (!formNodeElements || formNodeElements.length === 0) {
            return;
        }
        for (let i = 0; i < formNodeElements.length; i++) {
            const formNodeElement = formNodeElements[i];
            const dependentElement = formNodeElement.parentElement.parentElement.parentElement;
            if (dependentElement && dependentElement.className === 'hs-dependent-field') {
                formFields.push(formNodeElement.name);
            }
        }
    }

    public setFieldValue(name: string, value: string) {
        this.hubSpotApi.setFieldValue(name, value);
    }

    public getThankYouMessage() {
        return this.hubSpotApi._getState().form.thankYouMessage || '';
    }

    fetchHubSpotFormRedirectUrl() {
        this.hubspotProvider.getFormConfig(
            this.config.user.hubSpotPortalId,
            this.config.forms.hubspot.id
        )
        .then((response: any) => {
            this.redirectUrl = response.form.redirectUrl;
        });
    }

    getRedirectUrl() {
        return this.redirectUrl;
    }

    doesCaptchaExist() {
        const hubSpotFormNodes = this.hubSpotFormDOM.childNodes;
        if (!hubSpotFormNodes) {
            return false;
        }
        for (let i = 0; i < hubSpotFormNodes.length; i++) {
            const hubSpotFormNode: HTMLElement = hubSpotFormNodes[i];
            const cssClasses = hubSpotFormNode.className;
            if (cssClasses && cssClasses.indexOf('hs-recaptcha') > -1) {
                return true;
            }
        }
        return false;
    }

    showForm() {
        const hsForm = document.querySelector('.df-f-hubspot') as HTMLElement;
        if (hsForm) {
            hsForm.style.display = 'initial';
        }
    }

    hideForm() {
        const hsForm = document.querySelector('.df-f-hubspot') as HTMLElement;
        if (hsForm) {
            hsForm.style.display = 'none';
        }
    }

}
